import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import RecipeCard from '../components/RecipeCard';
import ShareButton from '../components/ShareButton';
import { getRecipeById, recipes } from '../data/recipes';

const RecipeDetailPage = () => {
  const { id } = useParams();
  const recipe = getRecipeById(id);
  const [voted, setVoted] = useState(false);

  if (!recipe) {
    return (
      <Inner>
        <NotFound>
          <i className="ti ti-mood-sad" aria-hidden="true" />
          <h1>Recette introuvable</h1>
          <BackBtn to="/recipes"><i className="ti ti-arrow-left" /> Retour aux recettes</BackBtn>
        </NotFound>
      </Inner>
    );
  }

  const total = (recipe.ingredients || []).reduce((s, i) => s + (Number(i.cost) || 0), 0);
  const related = recipes.filter((r) => r.category === recipe.category && r.id !== recipe.id).slice(0, 3);
  const votes = recipe.votes + (voted ? 1 : 0);

  return (
    <>
      <Inner>
        <BackBtn to="/recipes"><i className="ti ti-arrow-left" aria-hidden="true" /> Retour aux recettes</BackBtn>

        <Hero>
          <Photo src={recipe.image} alt={recipe.name} />
          <HeroInfo>
            <Tags>
              <Tag>{recipe.category}</Tag>
              {(recipe.occasions || []).map((o) => (
                <TagLight key={o}>{o}</TagLight>
              ))}
            </Tags>
            <h1>{recipe.name}</h1>
            <Lead>{recipe.description}</Lead>

            <MetaRow>
              <Meta><i className="ti ti-clock" /> <b>{recipe.prep_time} min</b><span>préparation</span></Meta>
              <Meta><i className="ti ti-star" /> <b>{recipe.rating.toFixed(1)}/5</b><span>{votes.toLocaleString('fr-FR')} votes</span></Meta>
              <Meta><i className="ti ti-coin" /> <b>{recipe.estimated_cost.toLocaleString('fr-FR')} FCFA</b><span>coût estimé</span></Meta>
            </MetaRow>

            <Author><i className="ti ti-user" aria-hidden="true" /> Recette de <b>{recipe.author}</b></Author>

            <Actions>
              <VoteBtn $voted={voted} onClick={() => setVoted((v) => !v)}>
                <i className={voted ? 'ti ti-check' : 'ti ti-heart'} aria-hidden="true" />
                {voted ? 'Vote enregistré · Merci !' : "J'aime cette recette"}
              </VoteBtn>
              <ShareButton title={`${recipe.name} — Tartina`} text={recipe.description} />
            </Actions>
          </HeroInfo>
        </Hero>

        <Columns>
          <IngCard>
            <CardTitle><i className="ti ti-basket" aria-hidden="true" /> Ingrédients</CardTitle>
            <TartinaPill><i className="ti ti-droplet" /> {recipe.tartina_quantity} de Tartina</TartinaPill>
            <IngList>
              {recipe.ingredients.map((ing, i) => (
                <IngItem key={i}>
                  <span>{ing.name} {ing.quantity && <em>· {ing.quantity}{ing.unit && ` ${ing.unit}`}</em>}</span>
                  <Cost>{Number(ing.cost).toLocaleString('fr-FR')} F</Cost>
                </IngItem>
              ))}
            </IngList>
            <TotalRow><span>Total estimé</span><b>{total.toLocaleString('fr-FR')} FCFA</b></TotalRow>
          </IngCard>

          <Steps>
            <CardTitle><i className="ti ti-list-numbers" aria-hidden="true" /> Préparation</CardTitle>
            <StepList>
              {recipe.steps.map((s, i) => (
                <Step key={i}>
                  <StepNum>{i + 1}</StepNum>
                  <p>{s}</p>
                </Step>
              ))}
            </StepList>

            {recipe.tips?.length > 0 && (
              <Tips>
                <TipsTitle><i className="ti ti-bulb" aria-hidden="true" /> Astuces Tartina</TipsTitle>
                <ul>{recipe.tips.map((t, i) => <li key={i}>{t}</li>)}</ul>
              </Tips>
            )}
          </Steps>
        </Columns>

        {related.length > 0 && (
          <Related>
            <h2>Tu aimeras aussi</h2>
            <Grid>{related.map((r) => <RecipeCard key={r.id} recipe={r} />)}</Grid>
          </Related>
        )}
      </Inner>
    </>
  );
};

const Inner = styled.div`max-width: 1100px; margin: 0 auto; padding: 28px 24px 0;`;
const BackBtn = styled(Link)`
  display: inline-flex; align-items: center; gap: 6px; text-decoration: none; font-weight: 800;
  color: ${({ theme }) => theme.colors.tertiary}; margin-bottom: 20px;
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;
const Hero = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 32px; align-items: center;
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;
const Photo = styled.img`
  width: 100%; border-radius: 24px; aspect-ratio: 4/3; object-fit: cover;
  box-shadow: ${({ theme }) => theme.shadows.md};
`;
const HeroInfo = styled.div`
  h1 { font-size: clamp(1.8rem, 3.6vw, 2.6rem); color: ${({ theme }) => theme.colors.chocolate}; margin: 12px 0 10px; }
`;
const Tags = styled.div`display: flex; gap: 8px;`;
const Tag = styled.span`
  background: ${({ theme }) => theme.colors.secondary}; color: ${({ theme }) => theme.colors.chocolate};
  font-weight: 800; font-size: 0.78rem; padding: 5px 12px; border-radius: 999px; text-transform: capitalize;
`;
const TagLight = styled(Tag)`
  background: ${({ theme }) => theme.colors.cream}; color: ${({ theme }) => theme.colors.tertiary};
`;
const Lead = styled.p`color: ${({ theme }) => theme.colors.textSecondary}; font-size: 1.05rem; margin: 0 0 20px;`;
const MetaRow = styled.div`display: flex; gap: 14px; flex-wrap: wrap; margin-bottom: 18px;`;
const Meta = styled.div`
  flex: 1; min-width: 110px; background: ${({ theme }) => theme.colors.background};
  border-radius: 16px; padding: 12px 14px; display: flex; flex-direction: column;
  i { font-size: 1.2rem; color: ${({ theme }) => theme.colors.primary}; margin-bottom: 4px; }
  b { font-family: ${({ theme }) => theme.fonts.heading}; font-size: 1.05rem; color: ${({ theme }) => theme.colors.chocolate}; }
  span { font-size: 0.78rem; color: ${({ theme }) => theme.colors.textSecondary}; }
`;
const Author = styled.p`
  display: inline-flex; align-items: center; gap: 6px; color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.95rem; margin: 0 0 18px; b { color: ${({ theme }) => theme.colors.chocolate}; }
  i { color: ${({ theme }) => theme.colors.secondaryDark}; }
`;
const Actions = styled.div`
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
`;
const VoteBtn = styled.button`
  display: inline-flex; align-items: center; gap: 8px; cursor: pointer;
  border: none; border-radius: 999px; padding: 13px 24px; font-weight: 800; font-size: 1rem;
  background: ${({ $voted, theme }) => ($voted ? theme.colors.success : theme.colors.primary)};
  color: #fff; transition: all .2s ease;
  &:hover { transform: translateY(-2px); }
  i { font-size: 1.15rem; }
`;
const Columns = styled.div`
  display: grid; grid-template-columns: 0.9fr 1.1fr; gap: 28px; margin-top: 40px;
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;
const CardTitle = styled.h2`
  display: flex; align-items: center; gap: 8px; font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.chocolate}; margin-bottom: 16px;
  i { color: ${({ theme }) => theme.colors.primary}; }
`;
const IngCard = styled.div`
  background: #fff; border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 20px; padding: 24px; align-self: start;
`;
const TartinaPill = styled.div`
  display: inline-flex; align-items: center; gap: 6px; background: ${({ theme }) => theme.colors.cream};
  color: ${({ theme }) => theme.colors.tertiary}; font-weight: 800; font-size: 0.85rem;
  padding: 6px 12px; border-radius: 999px; margin-bottom: 14px;
`;
const IngList = styled.div``;
const IngItem = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 12px;
  padding: 11px 0; border-bottom: 1px dashed ${({ theme }) => theme.colors.border};
  em { color: ${({ theme }) => theme.colors.textSecondary}; font-style: normal; font-size: 0.9rem; }
`;
const Cost = styled.span`color: ${({ theme }) => theme.colors.tertiary}; font-weight: 800; font-size: 0.85rem; white-space: nowrap;`;
const TotalRow = styled.div`
  display: flex; justify-content: space-between; align-items: center; margin-top: 16px;
  background: ${({ theme }) => theme.colors.background}; border-radius: 14px; padding: 14px 16px;
  span { font-weight: 700; color: ${({ theme }) => theme.colors.textSecondary}; }
  b { font-family: ${({ theme }) => theme.fonts.heading}; font-size: 1.25rem; color: ${({ theme }) => theme.colors.primary}; }
`;
const Steps = styled.div``;
const StepList = styled.div`display: flex; flex-direction: column; gap: 14px;`;
const Step = styled.div`
  display: flex; gap: 14px; align-items: flex-start;
  p { margin: 0; color: ${({ theme }) => theme.colors.textPrimary}; line-height: 1.6; padding-top: 4px; }
`;
const StepNum = styled.span`
  flex: none; width: 34px; height: 34px; border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary}; color: #fff;
  display: flex; align-items: center; justify-content: center; font-weight: 800;
  font-family: ${({ theme }) => theme.fonts.heading};
`;
const Tips = styled.div`
  margin-top: 24px; background: ${({ theme }) => theme.colors.sun};
  border-radius: 18px; padding: 20px 22px;
  ul { margin: 8px 0 0; padding-left: 20px; }
  li { color: ${({ theme }) => theme.colors.chocolate}; margin-bottom: 6px; }
`;
const TipsTitle = styled.div`
  display: flex; align-items: center; gap: 8px; font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: 600; font-size: 1.15rem; color: ${({ theme }) => theme.colors.chocolate};
`;
const Related = styled.section`
  margin-top: 56px; padding-bottom: 16px;
  h2 { font-size: 1.7rem; color: ${({ theme }) => theme.colors.chocolate}; margin-bottom: 20px; }
`;
const Grid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 26px;`;
const NotFound = styled.div`
  text-align: center; padding: 80px 20px;
  i { font-size: 3rem; color: ${({ theme }) => theme.colors.secondaryDark}; }
  h1 { margin: 12px 0 20px; color: ${({ theme }) => theme.colors.chocolate}; }
`;

export default RecipeDetailPage;
